// ===========================================
// Offer-to-Partner Dialog (partner classes)
// ===========================================
// When saving an INDIVIDUAL class conflicts with a partner-ELIGIBLE lesson (same teacher/term/day,
// both partner-enabled), instead of a bare "override or not" we offer to make them a partner class —
// which legitimises the overlap via the existing partner exception (NO overrideConflicts). Makes
// pairing discoverable at the moment it matters.

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';

export interface PartnerOfferDialogProps {
  open: boolean;
  /** Name of the lesson this one overlaps (the eligible partner candidate). */
  conflictLessonName: string;
  /** Name of the current partner that pairing would REPLACE, if the edited lesson already has one. */
  currentPartnerName?: string | null;
  loading?: boolean;
  /** Pair with the conflicting lesson (re-submit with partnerLessonId). */
  onMakePartners: () => void;
  /** Treat it as a real conflict (fall through to the normal override path). */
  onItsAConflict: () => void;
}

export default function PartnerOfferDialog({
  open,
  conflictLessonName,
  currentPartnerName,
  loading = false,
  onMakePartners,
  onItsAConflict,
}: PartnerOfferDialogProps) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onItsAConflict} maxWidth="xs" fullWidth>
      <DialogTitle>Make these a partner class?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
          {`This class overlaps “${conflictLessonName}” for the same teacher on the same day.\n\n`}
          {'You can pair them as a partner class — both run in an overlapping window with the same ' +
            'teacher — or treat this as a real scheduling conflict.'}
          {currentPartnerName ? `\n\nPairing will unpair this class from “${currentPartnerName}”.` : ''}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onItsAConflict} disabled={loading}>
          It’s a conflict
        </Button>
        <Button
          onClick={onMakePartners}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          Make partners
        </Button>
      </DialogActions>
    </Dialog>
  );
}
